import {merge} from 'lodash';
import Assessment from '../assessment';
import AssessmentResult from '../../../values/AssessmentResult';
import {FAQS_POINTS, MEDIA_ID} from '@axyseo/const/analysis';

/**
 * Represents the assessment that checks if the text has any images present, including videos in product pages.
 */
export default class TextImagesAssessment extends Assessment {
  /**
   * Sets the identifier and the config.
   *
   * @param {object}  config      The configuration to use.
   * @param {boolean} countVideos Whether videos are also included in the assessment or not.
   *
   * @returns {void}
   */
  constructor(config = {}, countVideos = true) {
    super();

    const defaultConfig = {
      id: MEDIA_ID,
      docUrl:
        'https://docs.avada.io/seo-suite-help-center/seo-audit/on-page-seo/checklist#images-and-videos',
      priority: 'high',
      fixPosition: 'description',
      title: 'Media',
      content: {
        good: 'Media are used effectively.',
        improve: '',
        bad: 'No media found. Add visual content to enhance engagement.'
      }
    };

    this.identifier = MEDIA_ID;
    this._config = merge(defaultConfig, config);
    this._countVideos = countVideos;
  }

  /**
   * Execute the Assessment and return a result.
   *
   * @param {Paper}       paper       The Paper object to assess.
   * @param {Researcher}  researcher  The Researcher object containing all available researches.
   * @returns {AssessmentResult} The result of the assessment, containing both a score and a descriptive text.
   */
  getResult({paper, researcher}) {
    this.imageCount = researcher.getResearch('imageCount');
    this.videoCount = researcher.getResearch('videoCount');

    const assessmentResult = new AssessmentResult({config: this._config});

    const calculatedResult = this.calculateResult();

    assessmentResult.setScore(calculatedResult.score);
    assessmentResult.setStatus(calculatedResult.status);

    return assessmentResult;
  }

  /**
   * Checks whether the paper has text.
   *
   * @param {Paper}       paper       The paper to use for the assessment.
   *
   * @returns {boolean} True when there is text.
   */
  isApplicable(paper) {
    return true;
  }

  /**
   *
   * @returns {{score: number, status: string}}
   */
  calculateResult() {
    // If "countVideos" is on, we include videos in the assessment
    const mediaCount = this._countVideos ? this.imageCount + this.videoCount : this.imageCount;

    let status = 'good';
    // No images.
    if (mediaCount === 0) {
      status = 'bad';
    }

    const score = this.getScore(FAQS_POINTS, status);

    return {
      score,
      status
    };
  }
}
